import { Tooltip, Typography } from 'antd';
import {
	CheckCircleFilled,
	MinusCircleOutlined,
	WarningFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import type { CertLineItem, CertProposal } from '../../redux/api/material-certs';
import { partNumberLabel } from './buckets';

const { Text } = Typography;

/**
 * The bar Alro shipped, next to the bar ERP ordered.
 *
 * A reviewer's real question is "is this the same piece of metal", and the
 * honest way to answer it is to put the two descriptions side by side and let
 * the differences show. The verdict column is a prompt to look, not a
 * decision: a dash means one side never said, which is common on older lines
 * and is not the same as a disagreement.
 */

type Verdict = 'same' | 'different' | 'unknown';

interface Row {
	label: string;
	slip: string;
	erp: string;
	verdict: Verdict;
	/** What the slip literally said, when the cell shows a converted value. */
	slipNote?: string;
}

const DASH = '—';

/** Tolerance in inches. Alro rounds to thousandths; ERP sometimes to sixteenths. */
const SIZE_TOLERANCE = 0.02;

const num = (value: string | number | null | undefined): number | null => {
	if (value === null || value === undefined || value === '') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

/** Trim trailing zeros so 4.500 reads as 4.5, which is how the shop says it. */
const inches = (value: string | number | null | undefined): string => {
	const parsed = num(value);
	if (parsed === null) return '';
	return `${parseFloat(parsed.toFixed(3))}"`;
};

const words = (value: string | null | undefined): string =>
	(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Material names rarely match character for character -- the slip says
 * "6061-T6511 EXT ALUM" where ERP says "Aluminum 6061-T6" -- so agreement here
 * means one side's words are contained in the other's.
 */
const textVerdict = (slip: string, erp: string): Verdict => {
	const a = words(slip);
	const b = words(erp);
	if (!a || !b) return 'unknown';
	if (a === b) return 'same';
	const aParts = a.split(' ').filter((part) => part.length > 2);
	const bParts = b.split(' ').filter((part) => part.length > 2);
	if (!aParts.length || !bParts.length) return 'unknown';
	const overlap = aParts.filter((part) => b.includes(part)).length;
	const reverse = bParts.filter((part) => a.includes(part)).length;
	return overlap || reverse ? 'same' : 'different';
};

/**
 * Mirrors `_shape_ok` in the matcher. The slip and ERP use different
 * vocabularies -- Alro says how the bar was made, ERP says what form it is --
 * so "Extrusion" against "Rectangle Bar" is the same metal, and warning about
 * it on every extrusion would teach reviewers to ignore the column.
 */
const shapeVerdict = (slip: string, erp: string): Verdict => {
	if (!slip || !erp) return 'unknown';
	if (words(slip) === words(erp)) return 'same';
	if (slip === 'Extrusion' && (erp === 'Extrusion' || erp === 'Rectangle Bar')) {
		return 'same';
	}
	return 'different';
};

const numberVerdict = (
	slip: string | number | null | undefined,
	erp: string | number | null | undefined,
	tolerance = SIZE_TOLERANCE
): Verdict => {
	const a = num(slip);
	const b = num(erp);
	if (a === null || b === null) return 'unknown';
	return Math.abs(a - b) <= tolerance ? 'same' : 'different';
};

/**
 * A cross-section reads differently per shape: rounds have a diameter, bars a
 * height and a width, tube a wall. Show whichever the shape actually has so
 * the row stays one line.
 */
const crossSection = (
	height: string | number | null,
	width: string | number | null,
	outer: string | number | null,
	inner: string | number | null,
	wall?: string | number | null
): string => {
	const parts: string[] = [];
	if (num(outer) !== null) {
		parts.push(`${inches(outer)} OD`);
		if (num(inner) !== null) parts.push(`${inches(inner)} ID`);
	}
	if (num(height) !== null && num(width) !== null) {
		parts.push(`${inches(height)} x ${inches(width)}`);
	} else if (num(height) !== null) {
		parts.push(inches(height));
	} else if (num(width) !== null) {
		parts.push(inches(width));
	}
	if (num(wall) !== null) parts.push(`${inches(wall)} wall`);
	return parts.join(' · ');
};

const same = (
	slip: string | number | null | undefined,
	erp: string | number | null | undefined
) => numberVerdict(slip, erp) === 'same';

/**
 * Round stock is judged on diameters and wall, rectangular on height and
 * width. Height and width are compared both ways round because a slip reading
 * "1/2 X 1" says nothing about which of the two ERP calls the height, the same
 * allowance `_size_ok` makes.
 */
const sizeVerdict = (proposal: CertProposal, line: CertLineItem | null): Verdict => {
	if (!line) return 'unknown';

	if (proposal.outer_diameter !== null) {
		if (
			line.outer_diameter === null &&
			line.inner_diameter === null &&
			line.wall_thickness === null
		) {
			return 'unknown';
		}
		return same(proposal.outer_diameter, line.outer_diameter) ||
			same(proposal.inner_diameter, line.inner_diameter) ||
			same(proposal.wall_thickness, line.wall_thickness)
			? 'same'
			: 'different';
	}

	if (proposal.height === null || proposal.width === null) return 'unknown';
	if (line.height === null && line.width === null) return 'unknown';
	const straight = same(proposal.height, line.height) && same(proposal.width, line.width);
	const swapped = same(proposal.height, line.width) && same(proposal.width, line.height);
	return straight || swapped ? 'same' : 'different';
};

const buildRows = (proposal: CertProposal): Row[] => {
	const line = proposal.line_item;

	const slipSize = crossSection(
		proposal.height,
		proposal.width,
		proposal.outer_diameter,
		proposal.inner_diameter,
		proposal.wall_thickness
	);
	const erpSize = line
		? crossSection(
				line.height,
				line.width,
				line.outer_diameter,
				line.inner_diameter,
				line.wall_thickness
			)
		: '';

	// ERP records lengths in inches only, so the slip's own units get in the
	// way of the comparison: `12 FT` beside `144"` reads as a disagreement.
	// Show the converted figure and keep Alro's wording behind it.
	const slipLength = inches(proposal.length_inches) || proposal.length_raw;
	const erpLength = inches(line?.length);
	const lengthNote =
		proposal.length_raw && slipLength !== proposal.length_raw
			? `The slip says ${proposal.length_raw}`
			: undefined;

	const erpPart = (line?.vendor_part_number || '').trim();
	const slipPart = proposal.edp || (proposal.part_hint ? `${proposal.part_hint}…` : '');
	const partVerdict: Verdict =
		!erpPart || !slipPart
			? 'unknown'
			: proposal.edp
				? proposal.edp.toUpperCase() === erpPart.toUpperCase()
					? 'same'
					: 'different'
				: erpPart.toUpperCase().startsWith(proposal.part_hint.toUpperCase())
					? 'same'
					: 'different';

	return [
		{
			label: partNumberLabel(proposal.vendor_key),
			slip: slipPart,
			erp: erpPart,
			slipNote:
				!proposal.edp && proposal.part_hint
					? `Requested cert names part ${proposal.part_hint}, the start of an EDP`
					: undefined,
			verdict: partVerdict,
		},
		{
			label: 'Material',
			slip: proposal.type_token || proposal.description,
			erp: line?.type_name || '',
			verdict: textVerdict(
				proposal.type_token || proposal.description,
				line?.type_name || ''
			),
		},
		{
			label: 'Shape',
			slip: proposal.shape,
			erp: line?.shape_name || '',
			verdict: shapeVerdict(proposal.shape, line?.shape_name || ''),
		},
		{
			label: 'Size',
			slip: slipSize,
			erp: erpSize,
			verdict: sizeVerdict(proposal, line),
		},
		{
			label: 'Length',
			slip: slipLength,
			erp: erpLength,
			slipNote: lengthNote,
			// Sixteenths on a saw cut, so a looser tolerance than cross-section.
			verdict: numberVerdict(proposal.length_inches, line?.length, 0.0625),
		},
		{
			label: 'Date',
			slip: proposal.received_at
				? dayjs(proposal.received_at).format('MMM D, YYYY')
				: '',
			erp: proposal.order_date
				? dayjs(proposal.order_date).format('MMM D, YYYY')
				: '',
			// Dates are context, never a reason to reject a match.
			verdict: 'unknown',
		},
	];
};

const VERDICT_ICON: Record<Verdict, JSX.Element> = {
	same: (
		<Tooltip title="These agree.">
			<CheckCircleFilled style={{ color: '#52c41a' }} />
		</Tooltip>
	),
	different: (
		<Tooltip title="These disagree — read the certificate before matching.">
			<WarningFilled style={{ color: '#faad14' }} />
		</Tooltip>
	),
	unknown: (
		<Tooltip title="One side does not say.">
			<MinusCircleOutlined style={{ color: '#bfbfbf' }} />
		</Tooltip>
	),
};

export default function MatchComparison({ proposal }: { proposal: CertProposal }) {
	const rows = buildRows(proposal);
	const cell: React.CSSProperties = {
		padding: '4px 8px',
		borderTop: '1px solid #f0f0f0',
		fontSize: 13,
		verticalAlign: 'top',
	};
	const labelCell: React.CSSProperties = { ...cell, whiteSpace: 'nowrap' };

	return (
		<table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
			<thead>
				<tr>
					<th style={{ ...cell, borderTop: 0, width: 78, textAlign: 'left' }} />
					<th style={{ ...cell, borderTop: 0, textAlign: 'left' }}>
						<Text type="secondary" style={{ fontSize: 11 }}>
							{proposal.vendor_key === 'mcmaster' ? 'MCMASTER' : 'ALRO SLIP'}
						</Text>
					</th>
					<th style={{ ...cell, borderTop: 0, textAlign: 'left' }}>
						<Text type="secondary" style={{ fontSize: 11 }}>
							ERP LINE
						</Text>
					</th>
					<th style={{ ...cell, borderTop: 0, width: 24 }} />
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={row.label}>
						<td style={labelCell}>
							<Text type="secondary">{row.label}</Text>
						</td>
						<td style={cell}>
							{!row.slip ? (
								<Text type="secondary">{DASH}</Text>
							) : row.slipNote ? (
								<Tooltip title={row.slipNote}>
									<Text style={{ borderBottom: '1px dotted #bfbfbf' }}>
										{row.slip}
									</Text>
								</Tooltip>
							) : (
								row.slip
							)}
						</td>
						<td
							style={{
								...cell,
								color: row.verdict === 'different' ? '#d46b08' : undefined,
							}}
						>
							{row.erp || <Text type="secondary">{DASH}</Text>}
						</td>
						<td style={{ ...cell, textAlign: 'center' }}>
							{VERDICT_ICON[row.verdict]}
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}
